import { useEffect, useState, type FormEvent } from "react";
import type { MigrationJob, PanelUser, ServerNode } from "../types";
import { api } from "../api";

export function MigrationsPage({
  accounts,
  servers = [],
  currentUser,
  onRefreshAccounts
}: {
  accounts: PanelUser[];
  servers?: ServerNode[];
  currentUser: PanelUser;
  onRefreshAccounts: () => Promise<void>;
}) {
  const staff =
    currentUser.role === "superadmin" || currentUser.role === "admin";
  const [jobs, setJobs] = useState<MigrationJob[]>([]);
  const [status, setStatus] = useState<Record<string, unknown> | null>(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [userId, setUserId] = useState(
    staff ? accounts.find((a) => a.role === "user")?.id || "" : currentUser.id
  );
  const [file, setFile] = useState<File | null>(null);
  const [lastInspect, setLastInspect] = useState<MigrationJob | null>(null);
  const [importHome, setImportHome] = useState(true);
  const [importDatabases, setImportDatabases] = useState(true);
  const [targetServerId, setTargetServerId] = useState(
    servers.find((s) => s.isDefault)?.id || servers[0]?.id || ""
  );

  const load = async () => {
    try {
      const [j, s] = await Promise.all([
        api<{ ok: boolean; items: MigrationJob[] }>("/api/v1/migrations"),
        api<{ ok: boolean; status: Record<string, unknown> }>(
          "/api/v1/migrations/status"
        )
      ]);
      setJobs(j.items || []);
      setStatus(s.status || null);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  };

  useEffect(() => {
    void load();
  }, []);

  const readFileBase64 = (f: File): Promise<string> =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const result = String(reader.result || "");
        const b64 = result.includes(",") ? result.split(",")[1]! : result;
        resolve(b64);
      };
      reader.onerror = () => reject(reader.error);
      reader.readAsDataURL(f);
    });

  const onInspect = async (e: FormEvent) => {
    e.preventDefault();
    if (!file || !userId) {
      setError("Select account and archive file");
      return;
    }
    setBusy(true);
    setError("");
    try {
      const contentBase64 = await readFileBase64(file);
      const res = await api<{ ok: boolean; job: MigrationJob }>(
        "/api/v1/migrations/inspect",
        {
          method: "POST",
          body: JSON.stringify({
            userId,
            filename: file.name,
            contentBase64
          })
        }
      );
      setLastInspect(res.job);
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  };

  const onImport = async (dryRun: boolean) => {
    if (!lastInspect?.sourcePath || !userId) {
      setError("Run inspect first");
      return;
    }
    setBusy(true);
    setError("");
    try {
      await api("/api/v1/migrations/import", {
        method: "POST",
        body: JSON.stringify({
          userId,
          sourcePath: lastInspect.sourcePath,
          dryRun,
          importHome,
          importDatabases
        })
      });
      await load();
      await onRefreshAccounts();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  };

  const onExport = async () => {
    if (!userId) return;
    setBusy(true);
    setError("");
    try {
      await api("/api/v1/migrations/export", {
        method: "POST",
        body: JSON.stringify({ userId })
      });
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  };

  const onTransfer = async () => {
    if (!userId || !targetServerId) return;
    setBusy(true);
    setError("");
    try {
      await api("/api/v1/migrations/transfer", {
        method: "POST",
        body: JSON.stringify({ userId, targetServerId })
      });
      await load();
      await onRefreshAccounts();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  };

  const report = lastInspect?.report;

  return (
    <>
      <div className="topbar">
        <div>
          <h1>Transfer Tool</h1>
          <p>
            {staff
              ? "WHM · import cPanel archives, export packages, move accounts"
              : "Import / export your site data"}
          </p>
        </div>
        <div className="topbar-actions">
          <button
            type="button"
            className="btn btn-ghost"
            onClick={() => void load()}
          >
            Refresh
          </button>
        </div>
      </div>

      {error ? <div className="error-banner">{error}</div> : null}

      <div className="whm-stats" style={{ marginBottom: 14 }}>
        <div className="whm-stat">
          <span className="whm-stat-label">Jobs</span>
          <span className="whm-stat-value">{jobs.length}</span>
          <span className="whm-stat-meta">Inspect / import / export</span>
        </div>
        <div className="whm-stat">
          <span className="whm-stat-label">Formats</span>
          <span className="whm-stat-value" style={{ fontSize: 14 }}>
            {status
              ? Array.isArray(status.formats)
                ? (status.formats as string[]).join(", ")
                : "cPanel, RealPanel"
              : "—"}
          </span>
          <span className="whm-stat-meta">
            tar {String(status?.tarAvailable ?? "—")}
          </span>
        </div>
        <div className="whm-stat">
          <span className="whm-stat-label">Last inspect</span>
          <span className="whm-stat-value" style={{ fontSize: 16 }}>
            {lastInspect?.status || "—"}
          </span>
          <span className="whm-stat-meta mono">
            {lastInspect?.format || ""}
          </span>
        </div>
        <div className="whm-stat">
          <span className="whm-stat-label">Tools</span>
          <div className="row-actions" style={{ marginTop: 6, flexWrap: "wrap" }}>
            <span className="chip">Import cPanel</span>
            <span className="chip">Export</span>
            {staff ? <span className="chip">Transfer</span> : null}
          </div>
        </div>
      </div>

      {status ? (
        <section className="card" style={{ marginBottom: 16 }}>
          <div className="card-label">Transfer engine</div>
          <p className="card-meta mono">
            root: {String(status.migrateRoot || "—")} · tar:{" "}
            {String(status.tarAvailable)}
          </p>
        </section>
      ) : null}

      <form className="panel-form" onSubmit={(e) => void onInspect(e)}>
        <div className="panel-form-title">
          1. Upload &amp; inspect (cPanel / RealPanel archive)
        </div>
        <div className="form-grid">
          <div className="field">
            <label>Target account</label>
            <select
              value={userId}
              onChange={(e) => setUserId(e.target.value)}
              required
            >
              <option value="">Select…</option>
              {(staff
                ? accounts.filter((a) => a.role === "user")
                : accounts
              ).map((a) => (
                <option key={a.id} value={a.id}>
                  {a.username}
                  {a.primaryDomain ? ` · ${a.primaryDomain}` : ""}
                  {a.provisionStatus !== "ready" ? " (not provisioned)" : ""}
                  {a.serverName ? ` · ${a.serverName}` : ""}
                </option>
              ))}
            </select>
          </div>
          <div className="field">
            <label>Backup archive (.tar.gz)</label>
            <input
              type="file"
              accept=".tar,.tar.gz,.tgz,.gz"
              onChange={(e) => setFile(e.target.files?.[0] || null)}
            />
          </div>
        </div>
        <button
          className="btn btn-primary btn-inline"
          disabled={busy}
          type="submit"
        >
          {busy ? "Working…" : "Upload & inspect archive"}
        </button>
      </form>

      {report ? (
        <section className="card" style={{ marginBottom: 16 }}>
          <div className="card-label">2. Inspect report</div>
          <dl className="kv compact">
            <dt>format</dt>
            <dd className="mono">{report.format}</dd>
            <dt>can import</dt>
            <dd>{report.canImport ? "yes" : "no"}</dd>
            <dt>username</dt>
            <dd className="mono">{report.detectedUsername || "—"}</dd>
            <dt>domains</dt>
            <dd className="mono">{report.domains.join(", ") || "—"}</dd>
            <dt>databases</dt>
            <dd className="mono">{report.databases.join(", ") || "—"}</dd>
            <dt>homedir / mysql / mail</dt>
            <dd className="mono">
              {String(report.hasHomedir)} / {String(report.hasMysql)} /{" "}
              {String(report.hasMail)}
            </dd>
          </dl>
          {report.notes?.length ? (
            <ul className="card-meta">
              {report.notes.map((n) => (
                <li key={n}>{n}</li>
              ))}
            </ul>
          ) : null}
          {report.skips?.length ? (
            <div style={{ marginTop: 10 }}>
              <div className="card-label">Skipped / limitations</div>
              <ul className="card-meta">
                {report.skips.map((s) => (
                  <li key={s.item}>
                    <strong>{s.item}</strong>: {s.reason}
                  </li>
                ))}
              </ul>
            </div>
          ) : null}

          <div className="form-grid" style={{ marginTop: 12 }}>
            <div className="field checkbox-field">
              <label>
                <input
                  type="checkbox"
                  checked={importHome}
                  onChange={(e) => setImportHome(e.target.checked)}
                />{" "}
                Import home files
              </label>
            </div>
            <div className="field checkbox-field">
              <label>
                <input
                  type="checkbox"
                  checked={importDatabases}
                  onChange={(e) => setImportDatabases(e.target.checked)}
                />{" "}
                Stage SQL dumps into ~/databases
              </label>
            </div>
          </div>
          <div className="row-actions" style={{ marginTop: 12 }}>
            <button
              type="button"
              className="btn btn-ghost btn-inline"
              disabled={busy || !report.canImport}
              onClick={() => void onImport(true)}
            >
              Dry-run import
            </button>
            <button
              type="button"
              className="btn btn-primary btn-inline"
              disabled={busy || !report.canImport}
              onClick={() => void onImport(false)}
            >
              Apply import
            </button>
          </div>
        </section>
      ) : null}

      <section className="card" style={{ marginBottom: 16 }}>
        <div className="card-label">3. Export RealPanel package</div>
        <p className="card-meta">
          Creates a <code className="mono">realpanel-account-v1</code> tarball on
          the agent (home + SQL dumps under ~/databases).
        </p>
        <button
          type="button"
          className="btn btn-primary btn-inline"
          disabled={busy || !userId}
          onClick={() => void onExport()}
        >
          Export selected account
        </button>
      </section>

      {staff ? (
        <section className="card" style={{ marginBottom: 16 }}>
          <div className="card-label">4. Transfer between servers (advanced)</div>
          <p className="card-meta">
            Optional multi-node move. On a single server you can ignore this —
            use import/export above.
          </p>
          <div className="form-grid">
            <div className="field">
              <label>Target server</label>
              <select
                value={targetServerId}
                onChange={(e) => setTargetServerId(e.target.value)}
              >
                {servers.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.name} ({s.status})
                  </option>
                ))}
              </select>
            </div>
          </div>
          <button
            type="button"
            className="btn btn-primary btn-inline"
            disabled={busy || !userId || !targetServerId}
            onClick={() => void onTransfer()}
          >
            Transfer account
          </button>
        </section>
      ) : null}

      <div className="table-wrap">
        <table className="data-table">
          <thead>
            <tr>
              <th>When</th>
              <th>Action</th>
              <th>User</th>
              <th>Status</th>
              <th>Format</th>
              <th>Detail</th>
            </tr>
          </thead>
          <tbody>
            {jobs.length === 0 ? (
              <tr>
                <td colSpan={6} className="muted">
                  No migration jobs yet.
                </td>
              </tr>
            ) : (
              jobs.map((j) => (
                <tr key={j.id}>
                  <td className="mono" style={{ fontSize: 11 }}>
                    {new Date(j.createdAt).toLocaleString()}
                  </td>
                  <td>{j.action}</td>
                  <td className="mono">{j.username || j.userId?.slice(0, 8) || "—"}</td>
                  <td>
                    <span className={`badge status-${j.status === "ok" ? "active" : j.status === "failed" ? "suspended" : "active"}`}>
                      {j.status}
                    </span>
                  </td>
                  <td className="mono">{j.format || "—"}</td>
                  <td className="muted" style={{ fontSize: 12, maxWidth: 280 }}>
                    {j.error ||
                      (j.result as { packagePath?: string } | null)?.packagePath ||
                      j.sourcePath ||
                      "—"}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </>
  );
}
